import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuProps,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent,
  CardActions,
  Grid,
  Stack,
  Pagination,
  InputAdornment,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  TextSnippet as TemplateIcon,
  Visibility as VisibilityIcon,
  FileCopy as FileCopyIcon,
  Analytics as AnalyticsIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { 
  MessageTemplate, 
  CreateMessageTemplateRequest, 
  messageTemplatesAPI, 
  templateHelpers 
} from '../../services/api/messageTemplates.service';

interface TemplateManagementProps {
  brandId: string;
  brandName?: string;
}

interface TemplateFormData {
  template_name: string;
  template_content: string;
  template_type: string;
  platform: string;
  category: string;
  sentiment: string;
  is_active: boolean;
  is_default: boolean;
  description: string;
  variables: Record<string, string>;
}

const TemplateManagement: React.FC<TemplateManagementProps> = ({ brandId, brandName }) => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    template_type: '',
    platform: '',
    category: '',
    sentiment: '',
    is_active: true
  });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    template_name: '',
    template_content: '',
    template_type: 'reply',
    platform: 'instagram',
    category: '',
    sentiment: '',
    is_active: true,
    is_default: false,
    description: '',
    variables: {}
  });

  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTemplate, setMenuTemplate] = useState<MessageTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [brandId, page, searchQuery, filters]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await messageTemplatesAPI.getTemplates(brandId, {
        page,
        limit: 10,
        search: searchQuery || undefined,
        ...filters
      });
      setTemplates(response.data);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setDialogMode('create');
    setFormData({
      template_name: '',
      template_content: '',
      template_type: 'reply',
      platform: 'instagram',
      category: '',
      sentiment: '',
      is_active: true,
      is_default: false,
      description: '',
      variables: {}
    });
    setSelectedTemplate(null);
    setDialogOpen(true);
  };

  const handleEditTemplate = (template: MessageTemplate) => {
    setDialogMode('edit');
    setFormData({
      template_name: template.template_name,
      template_content: template.template_content,
      template_type: template.template_type,
      platform: template.platform || 'instagram',
      category: template.category || '',
      sentiment: template.sentiment || '',
      is_active: template.is_active,
      is_default: template.is_default,
      description: template.description || '',
      variables: template.variables || {}
    });
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  const handleViewTemplate = (template: MessageTemplate) => {
    setDialogMode('view');
    setFormData({
      template_name: template.template_name,
      template_content: template.template_content,
      template_type: template.template_type,
      platform: template.platform || 'instagram',
      category: template.category || '',
      sentiment: template.sentiment || '',
      is_active: template.is_active,
      is_default: template.is_default,
      description: template.description || '',
      variables: template.variables || {}
    });
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  const handleDeleteTemplate = async (template: MessageTemplate) => {
    if (window.confirm(`Are you sure you want to delete "${template.template_name}"?`)) {
      try {
        await messageTemplatesAPI.deleteTemplate(template.id);
        await loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
        setError('Failed to delete template');
      }
    }
  };

  const handleSaveTemplate = async () => {
    try {
      // Extract variables from template content
      const extractedVariables = templateHelpers.extractVariables(formData.template_content);
      const variables: Record<string, string> = {};
      extractedVariables.forEach(variable => {
        variables[variable] = 'string'; // Default type
      });

      const templateData = {
        ...formData,
        brand_id: brandId,
        variables
      };

      if (dialogMode === 'create') {
        await messageTemplatesAPI.createTemplate(templateData);
      } else if (dialogMode === 'edit' && selectedTemplate) {
        await messageTemplatesAPI.updateTemplate(selectedTemplate.id, templateData);
      }

      setDialogOpen(false);
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template');
    }
  };

  const handleCopyTemplate = async (template: MessageTemplate) => {
    try {
      await navigator.clipboard.writeText(template.template_content);
    } catch (error) {
      console.error('Failed to copy template:', error);
    }
  };

  const handleDuplicateTemplate = (template: MessageTemplate) => {
    setDialogMode('create');
    setFormData({
      template_name: `${template.template_name} (Copy)`,
      template_content: template.template_content,
      template_type: template.template_type,
      platform: template.platform || 'instagram',
      category: template.category || '',
      sentiment: template.sentiment || '',
      is_active: template.is_active,
      is_default: false, // Don't duplicate default flag
      description: template.description || '',
      variables: template.variables || {}
    });
    setSelectedTemplate(null);
    setDialogOpen(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, template: MessageTemplate) => {
    setAnchorEl(event.currentTarget);
    setMenuTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTemplate(null);
  };

  const renderTemplateDialog = () => {
    const isReadOnly = dialogMode === 'view';
    const templateVariables = templateHelpers.extractVariables(formData.template_content);

    return (
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {dialogMode === 'create' ? 'Create Template' : 
               dialogMode === 'edit' ? 'Edit Template' : 'View Template'}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Template Name"
                value={formData.template_name}
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                fullWidth
                required
                disabled={isReadOnly}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Template Type</InputLabel>
                <Select
                  value={formData.template_type}
                  onChange={(e) => setFormData({ ...formData, template_type: e.target.value })}
                  label="Template Type"
                  disabled={isReadOnly}
                >
                  <MenuItem value="reply">Reply</MenuItem>
                  <MenuItem value="greeting">Greeting</MenuItem>
                  <MenuItem value="follow_up">Follow Up</MenuItem>
                  <MenuItem value="closing">Closing</MenuItem>
                  <MenuItem value="complaint_response">Complaint Response</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  label="Platform"
                  disabled={isReadOnly}
                >
                  <MenuItem value="instagram">Instagram</MenuItem>
                  <MenuItem value="tiktok">TikTok</MenuItem>
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="">All Platforms</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                fullWidth
                disabled={isReadOnly}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Sentiment</InputLabel>
                <Select
                  value={formData.sentiment}
                  onChange={(e) => setFormData({ ...formData, sentiment: e.target.value })}
                  label="Sentiment"
                  disabled={isReadOnly}
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Positive">Positive</MenuItem>
                  <MenuItem value="Negative">Negative</MenuItem>
                  <MenuItem value="Neutral">Neutral</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Template Content"
                value={formData.template_content}
                onChange={(e) => setFormData({ ...formData, template_content: e.target.value })}
                fullWidth
                multiline
                rows={6}
                required
                disabled={isReadOnly}
                sx={{ mb: 2 }}
                helperText="Use {{variable_name}} for dynamic content"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                disabled={isReadOnly}
                sx={{ mb: 2 }}
                helperText="Describe when to use this template"
              />
            </Grid>

            {templateVariables.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Detected Variables:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {templateVariables.map((variable) => (
                    <Chip
                      key={variable}
                      label={variable}
                      size="small"
                      variant="outlined"
                      icon={<TemplateIcon />}
                    />
                  ))}
                </Stack>
              </Grid>
            )}

            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      disabled={isReadOnly}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                      disabled={isReadOnly}
                    />
                  }
                  label="Default Template"
                />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSaveTemplate}
              variant="contained"
              startIcon={<SaveIcon />}
            >
              {dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderTemplatesTable = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (templates.length === 0) {
      return (
        <Box textAlign="center" p={4}>
          <TemplateIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first message template to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTemplate}
          >
            Create Template
          </Button>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2">
                      {template.template_name}
                    </Typography>
                    {template.is_default && (
                      <StarIcon color="primary" fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={template.template_type}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={template.platform || 'All'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {template.category && (
                    <Chip
                      label={template.category}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {template.usage_count} times
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={template.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={template.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={(e) => handleMenuClick(e, template)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">
            Message Templates
            {brandName && (
              <Typography variant="body2" color="text.secondary">
                {brandName}
              </Typography>
            )}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTemplate}
          >
            Create Template
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" gap={2} mb={3}>
          <TextField
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.template_type}
              onChange={(e) => setFilters({ ...filters, template_type: e.target.value })}
              label="Type"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="reply">Reply</MenuItem>
              <MenuItem value="greeting">Greeting</MenuItem>
              <MenuItem value="follow_up">Follow Up</MenuItem>
              <MenuItem value="closing">Closing</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Platform</InputLabel>
            <Select
              value={filters.platform}
              onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
              label="Platform"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="instagram">Instagram</MenuItem>
              <MenuItem value="tiktok">TikTok</MenuItem>
              <MenuItem value="google">Google</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {renderTemplatesTable()}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {renderTemplateDialog()}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (menuTemplate) handleViewTemplate(menuTemplate);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <VisibilityIcon />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuTemplate) handleEditTemplate(menuTemplate);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuTemplate) handleCopyTemplate(menuTemplate);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ContentCopyIcon />
          </ListItemIcon>
          <ListItemText>Copy Content</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuTemplate) handleDuplicateTemplate(menuTemplate);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <FileCopyIcon />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            if (menuTemplate) handleDeleteTemplate(menuTemplate);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TemplateManagement;